/**
 * /admin/impersonate — mint a magic-link URL for any account.
 *
 * Backed by POST /api/admin/impersonate/magic-link (admin-only).
 * Every mint is audit-logged in `magic_link_admin_mints`. Prior
 * unused tokens for the same email are invalidated automatically
 * so only ONE live token exists per address at a time.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiErrorText } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  Copy,
  KeyRound,
  Loader2,
  ShieldAlert,
  Sparkles,
} from "lucide-react";

// Small preset buttons — the two most common test accounts + their
// preferred landing pages. Keeps the "sign in as our seeded QA
// accounts" flow one-click away.
const PRESETS = [
  {
    label: "Test Uni Owner (Oxford)",
    email: "test-owner@ox.ac.uk",
    university_id: "uk-oxford",
    redirect_hint: "",
  },
  {
    label: "Test Firm Owner (A&O Shearman)",
    email: "test-owner@aoshearman.com",
    university_id: "",
    redirect_hint: "/firm-portal/allen-overy",
  },
];

const TTL_OPTIONS = [
  { label: "30 min",  value: 30 },
  { label: "1 hour",  value: 60 },
  { label: "1 day",   value: 60 * 24 },
  { label: "1 week",  value: 60 * 24 * 7 },
];

export default function AdminImpersonate() {
  const [email, setEmail] = useState("");
  const [universityId, setUniversityId] = useState("");
  const [redirectHint, setRedirectHint] = useState("");
  const [ttl, setTtl] = useState(60);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const applyPreset = (p) => {
    setEmail(p.email);
    setUniversityId(p.university_id);
    setRedirectHint(p.redirect_hint);
    setResult(null);
    setError(null);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const clean = email.trim().toLowerCase();
    if (!clean || !clean.includes("@")) {
      toast.error("Enter an email address.");
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await api.post("/admin/impersonate/magic-link", {
        email: clean,
        university_id: universityId.trim() || undefined,
        redirect_hint: redirectHint.trim() || undefined,
        ttl_minutes: ttl,
      });
      setResult(data);
      toast.success("Magic-link minted");
    } catch (err) {
      const st = err?.response?.status;
      if (st === 401 || st === 403) {
        setError("Admin sign-in required for this page.");
      } else {
        setError(apiErrorText(err, "Mint failed"));
      }
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async () => {
    if (!result?.url) return;
    try {
      await navigator.clipboard.writeText(result.url);
      toast.success("URL copied");
    } catch {
      toast.error("Couldn't copy — select + copy manually.");
    }
  };

  return (
    <div
      className="max-w-2xl mx-auto px-6 py-10"
      data-testid="admin-impersonate-page"
    >
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900">
        <ArrowLeft className="h-3.5 w-3.5" /> Admin home
      </Link>

      <header className="mt-3">
        <div className="flex items-center gap-2 text-[#4C1D95]">
          <KeyRound className="h-4 w-4" />
          <span className="text-[10px] font-underground-sc tracking-widest">
            Impersonation
          </span>
        </div>
        <h1 className="mt-1 font-editorial text-3xl">
          Mint a magic-link URL
        </h1>
        <p className="mt-2 text-sm text-stone-600 max-w-xl">
          Generates a single-use magic-link URL for any email. Clicking
          the URL logs the browser in as that account. Every mint is
          audited in <span className="font-mono text-xs">magic_link_admin_mints</span> with your email attached.
        </p>
      </header>

      {/* Presets */}
      <div className="mt-6">
        <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-2">
          Presets
        </div>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.email}
              type="button"
              onClick={() => applyPreset(p)}
              data-testid={`admin-impersonate-preset-${p.email}`}
              className="px-3 py-1.5 rounded-full border border-stone-200 bg-white hover:border-[#4C1D95] hover:text-[#4C1D95] text-[12px] font-medium text-stone-700 transition-colors"
            >
              <Sparkles className="inline h-3 w-3 mr-1 text-[#4C1D95]" />
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <form
        onSubmit={submit}
        className="mt-6 space-y-4 p-5 border border-stone-200 rounded-xl bg-white"
        data-testid="admin-impersonate-form"
      >
        <div>
          <label htmlFor="ai-email" className="text-[11px] font-underground-sc tracking-wider text-stone-600">
            Email <span className="text-rose-600">*</span>
          </label>
          <Input
            id="ai-email"
            type="email"
            required
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="owner@ox.ac.uk"
            maxLength={200}
            data-testid="admin-impersonate-email"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="ai-uni" className="text-[11px] font-underground-sc tracking-wider text-stone-600">
              University id (optional)
            </label>
            <Input
              id="ai-uni"
              value={universityId}
              onChange={(e) => setUniversityId(e.target.value)}
              placeholder="uk-oxford"
              maxLength={120}
              data-testid="admin-impersonate-uni"
            />
            <p className="mt-1 text-[10px] text-stone-400 leading-snug">
              Auto-approves a uni claim on verify if the email domain
              matches the uni's official_domains.
            </p>
          </div>
          <div>
            <label htmlFor="ai-redir" className="text-[11px] font-underground-sc tracking-wider text-stone-600">
              Redirect after verify (optional)
            </label>
            <Input
              id="ai-redir"
              value={redirectHint}
              onChange={(e) => setRedirectHint(e.target.value)}
              placeholder="/firm-portal/allen-overy"
              maxLength={200}
              data-testid="admin-impersonate-redirect"
            />
            <p className="mt-1 text-[10px] text-stone-400 leading-snug">
              Where the browser goes after the token is exchanged.
              Defaults to /uni-portal.
            </p>
          </div>
        </div>

        <div>
          <label className="text-[11px] font-underground-sc tracking-wider text-stone-600">
            Link lifetime
          </label>
          <div className="mt-1 inline-flex rounded-lg border border-stone-200 bg-stone-50 p-0.5">
            {TTL_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => setTtl(o.value)}
                data-testid={`admin-impersonate-ttl-${o.value}`}
                className={
                  "px-3 py-1.5 rounded-md text-[12px] font-medium transition " +
                  (ttl === o.value
                    ? "bg-white border border-stone-300 text-stone-900 shadow-sm"
                    : "text-stone-500 hover:text-stone-800")
                }
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div className="pt-2">
          <Button
            type="submit"
            disabled={busy || !email.trim()}
            data-testid="admin-impersonate-submit"
          >
            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Mint magic-link
          </Button>
        </div>
      </form>

      {error && (
        <div
          className="mt-4 p-3 rounded-md border border-rose-200 bg-rose-50 text-sm text-rose-800 flex items-start gap-2"
          data-testid="admin-impersonate-error"
        >
          <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {result?.url && (
        <div
          className="mt-6 p-5 rounded-xl border border-emerald-200 bg-emerald-50/70"
          data-testid="admin-impersonate-result"
        >
          <div className="text-[10px] font-underground-sc tracking-widest text-emerald-800">
            Magic-link ready
          </div>
          <div className="mt-1 text-[12px] text-stone-600">
            For <span className="font-mono">{result.email}</span> · expires{" "}
            {new Date(result.expires_at).toLocaleString()}
          </div>
          <div className="mt-3 flex items-start gap-2 flex-wrap">
            <code
              className="flex-1 min-w-0 p-2 rounded border border-stone-200 bg-white text-[11px] font-mono break-all"
              data-testid="admin-impersonate-url"
            >
              {result.url}
            </code>
            <div className="flex gap-2 shrink-0">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={copyUrl}
                data-testid="admin-impersonate-copy"
              >
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy
              </Button>
              <a href={result.url} target="_blank" rel="noopener noreferrer">
                <Button
                  size="sm"
                  data-testid="admin-impersonate-open"
                >
                  Open in new tab
                </Button>
              </a>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-stone-500 leading-snug">
            Single use. Once clicked, mint a fresh one for the next
            login.
          </p>
        </div>
      )}
    </div>
  );
}
